import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { api } from "./api";
import { getDict } from "./i18n";
import type { Locale, Profile, Workspace } from "./types";

export type Role = "head_coach" | "coach" | "client";

export type Me = {
  profile: {
    id: string;
    username: string;
    full_name: string;
    locale: Locale;
    unit: "kg" | "lbs";
    is_sysadmin: boolean;
    must_change_password: boolean;
  };
  workspaces: { slug: string; name: string; role: Role }[];
};

export type WorkspaceCtx = {
  workspace: {
    id: string;
    slug: string;
    name: string;
    logo_url: string | null;
    primary_color: string;
    timezone: string;
    is_personal: boolean;
  };
  role: Role | null;
  is_sysadmin: boolean;
  is_head_coach: boolean;
  is_staff: boolean;
};

// Loaded once per session and kept: the profile and workspace rarely change, and every
// screen needs them. Mutations that change them invalidate these keys.
export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: () => api<Me>("/me"),
  staleTime: 5 * 60_000,
});

export const workspaceQuery = (slug: string) =>
  queryOptions({
    queryKey: ["w", slug, "context"],
    queryFn: () => api<WorkspaceCtx>(`/w/${encodeURIComponent(slug)}/context`),
    staleTime: 5 * 60_000,
  });

/** The dictionary for the user's language (Vietnamese before login). */
export function useT() {
  const { data } = useQuery({ ...meQuery, enabled: false });
  return getDict(data?.profile.locale ?? "vi");
}

/** The workspace of the current /w/$slug screen (already loaded by the layout). */
export function useWs() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { data } = useSuspenseQuery(workspaceQuery(slug));
  const me = useSuspenseQuery(meQuery).data;
  return {
    ...data,
    slug,
    base: `/w/${slug}`,
    me,
    profile: me.profile,
    t: getDict(me.profile.locale),
  };
}

/**
 * The same names the old server pages got from getWorkspaceCtx(), so ported screens keep their
 * code: workspace, profile, user, role, isStaff, isHeadCoach, isAdmin, t.
 */
export function useCtx() {
  const w = useWs();
  return {
    slug: w.slug,
    workspace: w.workspace as Workspace,
    profile: w.profile as Profile,
    user: { id: w.profile.id },
    role: w.role,
    isAdmin: w.is_sysadmin,
    isHeadCoach: w.is_head_coach,
    isStaff: w.is_staff,
    t: w.t,
  };
}

export type Ctx = ReturnType<typeof useCtx>;
